/**
 * ApplicationsPage Component
 *
 * Main page for managing applications with search and filtering.
 * Follows the same approach as GroupsPage for UI consistency.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { ApplicationTable } from '../components/ApplicationTable';
import { CreateApplicationModal } from '../components/CreateApplicationModal';
import { AISetupModal } from '../components/AISetupModal';
import { ApplicationDetailsModal } from '../components/ApplicationDetailsModal';
import { UpdateApplicationModal } from '../components/UpdateApplicationModal';
import { DeploymentsModal } from '../components/DeploymentsModal';
import { LogsModal } from '../components/LogsModal';
import { PermissionsModal } from '../components/PermissionsModal';
import { EnvironmentVariablesModal } from '../components/EnvironmentVariablesModal';
import { DeleteApplicationModal } from '../components/DeleteApplicationModal';
import { DeploymentTokensModal } from '../components/DeploymentTokensModal';
import { useApplications } from '../hooks/useApplications';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { ApplicationSanitized } from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * ApplicationsPage component
 */
export const ApplicationsPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showAISetupModal, setShowAISetupModal] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<ApplicationSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeploymentsModal, setShowDeploymentsModal] = useState<boolean>(false);
  const [showLogsModal, setShowLogsModal] = useState<boolean>(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState<boolean>(false);
  const [showEnvVarsModal, setShowEnvVarsModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeploymentTokensModal, setShowDeploymentTokensModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'applications:application:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'applications:application:update');
  const canDelete = hasPermission(activeRole?.permissions, 'applications:application:delete');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch applications
  const {
    data: applications = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useApplications(tenantId, {
    search: debouncedSearch || undefined,
  });

  // Filter applications locally (additional filtering if needed)
  const filteredApplications = useMemo(() => {
    if (!applications) return [];

    let filtered = [...applications];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.applicationName.toLowerCase().includes(query) ||
          app.applicationCode.toLowerCase().includes(query) ||
          (app.description && app.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [applications, debouncedSearch]);

  const handleViewDetails = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowDetailsModal(true);
  };

  const handleEdit = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowUpdateModal(true);
  };

  const handleViewLogs = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowLogsModal(true);
  };

  const handleViewDeployments = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowDeploymentsModal(true);
  };

  const handleViewPermissions = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowPermissionsModal(true);
  };

  const handleViewEnvVars = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowEnvVarsModal(true);
  };

  const handleDelete = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowDeleteModal(true);
  };

  const handleViewDeploymentTokens = (application: ApplicationSanitized) => {
    setSelectedApp(application);
    setShowDeploymentTokensModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Applications</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage your custom applications and deployments
            </p>
          </div>
          {canCreate && (
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setShowAISetupModal(true)}>
                AI Setup
              </Button>
              <Button onClick={() => setShowCreateModal(true)}>
                Create Application
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Applications Table */}
        <ApplicationTable
          applications={filteredApplications}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewLogs={handleViewLogs}
          onViewDeployments={handleViewDeployments}
          onViewPermissions={handleViewPermissions}
          onViewEnvVars={handleViewEnvVars}
          onViewDeploymentTokens={handleViewDeploymentTokens}
          canEdit={canUpdate}
          canDelete={canDelete}
        />

        {/* Modals */}
        <CreateApplicationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        <AISetupModal
          isOpen={showAISetupModal}
          onClose={() => setShowAISetupModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedApp && (
          <ApplicationDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            applicationId={selectedApp.applicationId}
          />
        )}

        {showUpdateModal && selectedApp && (
          <UpdateApplicationModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            application={selectedApp}
          />
        )}

        {showDeploymentsModal && selectedApp && (
          <DeploymentsModal
            isOpen={showDeploymentsModal}
            onClose={() => {
              setShowDeploymentsModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            application={selectedApp}
          />
        )}

        {showLogsModal && selectedApp && (
          <LogsModal
            isOpen={showLogsModal}
            onClose={() => {
              setShowLogsModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            application={selectedApp}
          />
        )}

        {showPermissionsModal && selectedApp && (
          <PermissionsModal
            isOpen={showPermissionsModal}
            onClose={() => {
              setShowPermissionsModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            application={selectedApp}
            canManagePermissions={canUpdate}
          />
        )}

        {showEnvVarsModal && selectedApp && (
          <EnvironmentVariablesModal
            isOpen={showEnvVarsModal}
            onClose={() => {
              setShowEnvVarsModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            application={selectedApp}
          />
        )}

        {showDeleteModal && selectedApp && (
          <DeleteApplicationModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            application={selectedApp}
          />
        )}

        {showDeploymentTokensModal && selectedApp && (
          <DeploymentTokensModal
            isOpen={showDeploymentTokensModal}
            onClose={() => {
              setShowDeploymentTokensModal(false);
              setSelectedApp(null);
            }}
            tenantId={tenantId}
            applicationId={selectedApp.applicationId}
            applicationName={selectedApp.applicationName}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
